import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { DateRange } from '../primitives.types';

@Component({
  selector: 'kt-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateRangePickerComponent), multi: true }]
})
export class DateRangePickerComponent implements ControlValueAccessor {
  @Input() label = '';
  /** Where the label sits relative to the control. Default 'top'; use 'left'/'right' for inline layouts (e.g. toolbars with limited height). */
  @Input() labelPosition: 'top' | 'left' | 'right' | 'bottom' = 'top';
  /** When set, clamps the label to this many lines and reserves that height, so labels of
   * differing length stay vertically aligned across a row of fields. Unset = natural height. */
  @Input() labelLines: number = null;
  @Input() hint = '';
  @Input() errorMessage = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() startPlaceholder = 'Start date';
  @Input() endPlaceholder = 'End date';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  /** Calendar month to open at. */
  @Input() startAt: Date | null = null;
  /** Predicate to enable/disable individual dates (returns true for selectable dates). */
  @Input() dateFilter: (date: Date | null) => boolean = null;
  /** Panel class forwarded to the underlying mat-date-range-picker. */
  @Input() panelClass = '';

  /** Emits once both ends are picked. */
  @Output() rangeChange = new EventEmitter<DateRange>();

  range = new UntypedFormGroup({
    start: new UntypedFormControl(null),
    end: new UntypedFormControl(null)
  });

  onChange = (_: any) => { };
  onTouched = () => { };

  get dateRangeTooltip(): string {
    const start = this.range.value?.start;
    const end = this.range.value?.end;
    if (!start || !end) return '';
    const startStr = new Date(start).toLocaleDateString('en-US');
    const endStr = new Date(end).toLocaleDateString('en-US');
    return `${startStr} - ${endStr}`;
  }

  /** Fires on end-date selection — push the complete range up and notify. */
  onRangeInput() {
    const value: DateRange = { start: this.range.value.start, end: this.range.value.end };
    this.onChange(value);
    this.onTouched();
    if (value.start && value.end) {
      this.rangeChange.emit(value);
    }
  }

  writeValue(value: any) {
    this.range.setValue(
      { start: value?.start ?? null, end: value?.end ?? null },
      { emitEvent: false }
    );
  }
  registerOnChange(fn: any) { this.onChange = fn; }
  registerOnTouched(fn: any) { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    isDisabled ? this.range.disable({ emitEvent: false }) : this.range.enable({ emitEvent: false });
  }
}
